import {
  Calendar,
  MapPin,
  Users,
  ClipboardList,
  Settings,
  FileText,
  Activity,
  CheckCircle2,
  LogOut,
  PanelLeft,
  Menu,
  Bell,
  Database,
  AlertTriangle,
  History,
  UserCheck,
  ChevronLeft,
  Layers,
  DollarSign,
  Mail,
  BookUser,
  Clock,
  Plane,
  CalendarCheck,
  Trophy,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { NavLink } from "@/components/NavLink";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTheme } from "next-themes";
import { Moon, Sun } from "lucide-react";
import { NotificationsSidebar } from "@/components/NotificationsSidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useNavLayout } from "@/hooks/useNavLayout";
import { logAuditEvent } from "@/lib/auditLog";
import { usePageMetadata } from "@/contexts/PageMetadataContext";
import { useViewSize, isSmallOrMedium } from "@/hooks/useViewSize";
import { useEnvironment } from "@/contexts/EnvironmentContext";
import { useCoachView } from "@/contexts/CoachViewContext";

interface TopNavigationProps {
  isAdmin: boolean;
  isCoach: boolean;
  isAssistant?: boolean;
  profile?: any;
}

export function TopNavigation({
  isAdmin,
  isCoach,
  isAssistant = false,
  profile,
}: TopNavigationProps) {
  const navigate = useNavigate();
  const { toggleLayout } = useNavLayout();
  const { metadata } = usePageMetadata();
  const viewSize = useViewSize();
  const isMediumOrSmaller = isSmallOrMedium(viewSize);
  const isCompactView = viewSize === "xs" || viewSize === "sm";
  const { showDevData } = useEnvironment();
  const { isAdmin: canUseCoachView, viewAsCoach, toggleViewMode } =
    useCoachView();
  const { theme, setTheme } = useTheme();
  const coachViewLabel = viewAsCoach ? "Admin View" : "Coach View";

  const handleLogout = async () => {
    await logAuditEvent({
      action: "User logged out",
      category: "authentication",
      metadata: { userName: profile?.name },
    });
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const mainItems = [{ title: "Dashboard", url: "/dashboard", icon: Calendar }];

  const coachItems = isAssistant && !isCoach && !isAdmin
    ? [
        { title: "Sessions", url: "/sessions", icon: Calendar },
        { title: "Planners", url: "/planners", icon: CalendarCheck },
        { title: "Timesheets", url: "/timesheets", icon: Clock },
        { title: "Holidays", url: "/holidays", icon: Plane },
        { title: "My Documents", url: "/coach-documents", icon: FileText },
      ]
    : [
        { title: "Attendance", url: "/attendance", icon: ClipboardList },
        { title: "Planners", url: "/planners", icon: CalendarCheck },
        { title: "Tournaments", url: "/tournaments", icon: Trophy },
        { title: "Timesheets", url: "/timesheets", icon: Clock },
        { title: "Holidays", url: "/holidays", icon: Plane },
        { title: "My Documents", url: "/coach-documents", icon: FileText },
      ];

  const managementItems = [
    { title: "Venues", url: "/venues", icon: MapPin },
    { title: "Sessions", url: "/sessions", icon: Calendar },
    { title: "Bulk Sessions", url: "/bulk-sessions", icon: Layers },
    {
      title: "Attendance Approval",
      url: "/attendance-approval",
      icon: CheckCircle2,
    },
    { title: "Unpaid Attendance", url: "/unpaid-attendance", icon: DollarSign },
    { title: "Timesheet Approvals", url: "/timesheets/approvals", icon: Clock },
    { title: "Planner Manager", url: "/planners/admin", icon: CalendarCheck },
    { title: "Holidays", url: "/holidays", icon: Plane },
    { title: "Tournament Approvals", url: "/tournaments/approvals", icon: Trophy },
    { title: "Participant Book", url: "/participant-book", icon: BookUser },
    { title: "Incidents", url: "/incidents", icon: AlertTriangle },
    { title: "Mailshot", url: "/mailshot", icon: Mail },
    { title: "Coach Documents", url: "/coach-documents", icon: FileText },
    { title: "Audit Log", url: "/audit-log", icon: History },
  ];

  const configItems = [
    { title: "Users", url: "/users", icon: Users },
    { title: "Data Sync", url: "/data-sync", icon: Database },
    { title: "Settings", url: "/settings", icon: Settings },
  ];

  // Show compact header on small screens (xs/sm)
  if (isCompactView) {
    return (
      <header className="sticky top-0 z-50 flex h-14 items-center gap-4 border-b border-accent bg-sidebar text-sidebar-foreground dark:shadow-[0_5px_15px_7px_rgba(0,0,0,0.4)]  shadow-[0_0px_20px_10px_rgba(0,0,0,0.2)] px-4">
        {/* Back button - only shown when metadata.showBackButton is true */}
        {metadata?.showBackButton && metadata?.onBackClick && (
          <Button
            variant="ghost"
            size="icon"
            onClick={metadata.onBackClick}
            className="h-8 w-8"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
        )}

        {/* Logo */}
        <NavLink to="/" className="flex items-center">
          <img
            src="/logo_light.webp"
            alt="MicroSports Logo"
            className="block dark:hidden h-8 w-auto"
          />
          <img
            src="/logo_dark.webp"
            alt="MicroSports Logo"
            className="hidden dark:block h-8 w-auto"
          />
        </NavLink>
        {showDevData && (
          <Badge
            variant="outline"
            className="bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700 ml-2"
          >
            Dev
          </Badge>
        )}

        <div className="flex-1" />

        {/* Notifications - aligned right */}
        <NotificationsSidebar>
          <Button variant="ghost" size="icon">
            <Bell className="h-5 w-5" />
            <span className="sr-only">Notifications</span>
          </Button>
        </NotificationsSidebar>

        {/* Menu Button - moved to far right */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="h-6 w-6" />
              <span className="sr-only">Open navigation menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
              Main
            </DropdownMenuLabel>
            {mainItems.map((item) => (
              <DropdownMenuItem
                key={item.title}
                onClick={() => navigate(item.url)}
              >
                <item.icon className="mr-2 h-4 w-4" />
                {item.title}
              </DropdownMenuItem>
            ))}

            {(isCoach || isAdmin || isAssistant) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                  Coach
                </DropdownMenuLabel>
                {coachItems.map((item) => (
                  <DropdownMenuItem
                    key={item.title}
                    onClick={() => navigate(item.url)}
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    {item.title}
                  </DropdownMenuItem>
                ))}
              </>
            )}

            {isAdmin && !viewAsCoach && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                  Management
                </DropdownMenuLabel>
                {managementItems.map((item) => (
                  <DropdownMenuItem
                    key={item.title}
                    onClick={() => navigate(item.url)}
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    {item.title}
                  </DropdownMenuItem>
                ))}
              </>
            )}

            {isAdmin && !viewAsCoach && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                  Configuration
                </DropdownMenuLabel>
                {configItems.map((item) => (
                  <DropdownMenuItem
                    key={item.title}
                    onClick={() => navigate(item.url)}
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    {item.title}
                  </DropdownMenuItem>
                ))}
              </>
            )}

            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/profile")}>
              <Avatar className="h-4 w-4 mr-2">
                <AvatarImage src={profile?.profile_photo || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {(profile?.name || "")
                    .split(" ")
                    .map((n: string) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2) || "AD"}
                </AvatarFallback>
              </Avatar>
              Profile
            </DropdownMenuItem>
            {canUseCoachView && (
              <DropdownMenuItem onClick={toggleViewMode}>
                <UserCheck className="mr-2 h-4 w-4" />
                {coachViewLabel}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => setTheme(theme === "light" ? "dark" : "light")}
              className="flex items-center"
            >
              {theme === "dark" ? (
                <Moon className="mr-2 h-4 w-4" />
              ) : (
                <Sun className="mr-2 h-4 w-4" />
              )}
              {theme === "dark" ? "Dark Mode" : "Light Mode"}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleLogout}
              className="text-destructive cursor-pointer"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>
    );
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b border-accent bg-sidebar text-sidebar-foreground dark:shadow-[0_5px_15px_7px_rgba(0,0,0,0.4)]  shadow-[0_0px_20px_10px_rgba(0,0,0,0.2)]">
      <div className="flex h-16 items-center px-4 gap-4">
        {/* Logo - clickable to dashboard */}
        <NavLink to="/" className="flex items-center gap-2 mr-4">
          <img
            src="/logo_light.webp"
            alt="MicroSports"
            className="block dark:hidden h-10 object-contain"
          />
          <img
            src="/logo_dark.webp"
            alt="MicroSports"
            className="hidden dark:block h-10 object-contain"
          />
        </NavLink>
        {showDevData && (
          <Badge
            variant="outline"
            className="bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700 mr-2"
          >
            DEV MODE
          </Badge>
        )}

        {/* Navigation Items - Group buttons */}
        <nav className="flex items-center gap-1">
          {/* On medium screens and below: combine Main + Coach + Admin into single menu */}
          {isMediumOrSmaller && (isCoach || isAdmin || isAssistant) ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                >
                  <Menu className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                  Main
                </DropdownMenuLabel>
                {mainItems.map((item) => (
                  <DropdownMenuItem key={item.title} asChild>
                    <NavLink
                      to={item.url}
                      className="group/navlink flex items-center gap-2 w-full cursor-pointer [&>svg]:text-accent [&.text-sidebar-accent-foreground>svg]:!text-sidebar-accent-foreground hover:[&>svg]:!text-sidebar-accent-foreground"
                    >
                      <item.icon className="h-4 w-4 transition-colors group-hover/navlink:!text-sidebar-accent-foreground" />
                      <span>{item.title}</span>
                    </NavLink>
                  </DropdownMenuItem>
                ))}
                {(isCoach || isAdmin || isAssistant) && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                      Coach
                    </DropdownMenuLabel>
                    {coachItems.map((item) => (
                      <DropdownMenuItem key={item.title} asChild>
                        <NavLink
                          to={item.url}
                          className="group/navlink flex items-center gap-2 w-full cursor-pointer [&>svg]:text-accent [&.text-sidebar-accent-foreground>svg]:!text-sidebar-accent-foreground hover:[&>svg]:!text-sidebar-accent-foreground"
                        >
                          <item.icon className="h-4 w-4 transition-colors group-hover/navlink:!text-sidebar-accent-foreground" />
                          <span>{item.title}</span>
                        </NavLink>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
                {isAdmin && !viewAsCoach && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                      Management
                    </DropdownMenuLabel>
                    {managementItems.map((item) => (
                      <DropdownMenuItem key={item.title} asChild>
                        <NavLink
                          to={item.url}
                          className="group/navlink flex items-center gap-2 w-full cursor-pointer [&>svg]:text-accent [&.text-sidebar-accent-foreground>svg]:!text-sidebar-accent-foreground hover:[&>svg]:!text-sidebar-accent-foreground"
                        >
                          <item.icon className="h-4 w-4 transition-colors group-hover/navlink:!text-sidebar-accent-foreground" />
                          <span>{item.title}</span>
                        </NavLink>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                      Configuration
                    </DropdownMenuLabel>
                    {configItems.map((item) => (
                      <DropdownMenuItem key={item.title} asChild>
                        <NavLink
                          to={item.url}
                          className="group/navlink flex items-center gap-2 w-full cursor-pointer [&>svg]:text-accent [&.text-sidebar-accent-foreground>svg]:!text-sidebar-accent-foreground hover:[&>svg]:!text-sidebar-accent-foreground"
                        >
                          <item.icon className="h-4 w-4 transition-colors group-hover/navlink:!text-sidebar-accent-foreground" />
                          <span>{item.title}</span>
                        </NavLink>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              {/* On large screens: separate Main, Coach and Admin buttons */}
              {/* Main group */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  {isMediumOrSmaller ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        >
                          <Calendar className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Main</TooltipContent>
                    </Tooltip>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-2 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    >
                      <Calendar className="h-4 w-4" />
                      <span>Main</span>
                    </Button>
                  )}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  {mainItems.map((item) => (
                    <DropdownMenuItem key={item.title} asChild>
                      <NavLink
                        to={item.url}
                        className="group/navlink flex items-center gap-2 w-full cursor-pointer [&>svg]:text-accent [&.text-sidebar-accent-foreground>svg]:!text-sidebar-accent-foreground hover:[&>svg]:!text-sidebar-accent-foreground"
                      >
                        <item.icon className="h-4 w-4 transition-colors group-hover/navlink:!text-sidebar-accent-foreground" />
                        <span>{item.title}</span>
                      </NavLink>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Coach group */}
              {(isCoach || isAdmin || isAssistant) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    {isMediumOrSmaller ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                          >
                            <UserCheck className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Coach</TooltipContent>
                      </Tooltip>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                      >
                        <UserCheck className="h-4 w-4" />
                        <span>Coach</span>
                      </Button>
                    )}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    {coachItems.map((item) => (
                      <DropdownMenuItem key={item.title} asChild>
                        <NavLink
                          to={item.url}
                          className="group/navlink flex items-center gap-2 w-full cursor-pointer [&>svg]:text-accent [&.text-sidebar-accent-foreground>svg]:!text-sidebar-accent-foreground hover:[&>svg]:!text-sidebar-accent-foreground"
                        >
                          <item.icon className="h-4 w-4 transition-colors group-hover/navlink:!text-sidebar-accent-foreground" />
                          <span>{item.title}</span>
                        </NavLink>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Admin group */}
              {isAdmin && !viewAsCoach && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    {isMediumOrSmaller ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Admin</TooltipContent>
                      </Tooltip>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                      >
                        <Settings className="h-4 w-4" />
                        <span>Admin</span>
                      </Button>
                    )}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                      Management
                    </DropdownMenuLabel>
                    {managementItems.map((item) => (
                      <DropdownMenuItem key={item.title} asChild>
                        <NavLink
                          to={item.url}
                          className="group/navlink flex items-center gap-2 w-full cursor-pointer [&>svg]:text-accent [&.text-sidebar-accent-foreground>svg]:!text-sidebar-accent-foreground hover:[&>svg]:!text-sidebar-accent-foreground"
                        >
                          <item.icon className="h-4 w-4 transition-colors group-hover/navlink:!text-sidebar-accent-foreground" />
                          <span>{item.title}</span>
                        </NavLink>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                      Configuration
                    </DropdownMenuLabel>
                    {configItems.map((item) => (
                      <DropdownMenuItem key={item.title} asChild>
                        <NavLink
                          to={item.url}
                          className="group/navlink flex items-center gap-2 w-full cursor-pointer [&>svg]:text-accent [&.text-sidebar-accent-foreground>svg]:!text-sidebar-accent-foreground hover:[&>svg]:!text-sidebar-accent-foreground"
                        >
                          <item.icon className="h-4 w-4 transition-colors group-hover/navlink:!text-sidebar-accent-foreground" />
                          <span>{item.title}</span>
                        </NavLink>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          )}
        </nav>

        {/* Page info inline with separator */}
        {metadata.icon && (
          <>
            <div aria-hidden className="h-8 w-px bg-accent/40 rounded mx-4" />
            <div className="flex items-center gap-3 flex-1">
              <metadata.icon className="h-5 w-5 text-primary" />
              <div>
                <h1 className="text-base font-bold">{metadata.title}</h1>
                {metadata.subtitle && (
                  <p className="text-xs text-muted-foreground">
                    {metadata.subtitle}
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Right side controls */}
        <div className="flex items-center gap-2">
          {/* Notifications */}
          <NotificationsSidebar>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <Bell className="h-4 w-4" />
              <span className="sr-only">Notifications</span>
            </Button>
          </NotificationsSidebar>

          {/* Switch to sidebar layout */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={toggleLayout}
              >
                <PanelLeft className="h-4 w-4" />
                <span className="sr-only">Switch to sidebar</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Switch to sidebar</TooltipContent>
          </Tooltip>

          <ThemeToggle />

          {/* Profile dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={profile?.profile_photo || undefined} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {(profile?.name || "")
                      .split(" ")
                      .map((n: string) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2) || "AD"}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="flex items-center gap-2 p-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={profile?.profile_photo || undefined} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {(profile?.name || "")
                      .split(" ")
                      .map((n: string) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2) || "AD"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">
                    {profile?.name ?? "User"}
                  </span>
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <NavLink
                  to="/profile"
                  className="flex items-center gap-2 w-full cursor-pointer"
                >
                  Profile
                </NavLink>
              </DropdownMenuItem>
              {canUseCoachView && (
                <DropdownMenuItem onClick={toggleViewMode}>
                  <UserCheck className="h-4 w-4 mr-2" />
                  {coachViewLabel}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => setTheme(theme === "light" ? "dark" : "light")}
              >
                {theme === "dark" ? (
                  <Moon className="h-4 w-4 mr-2" />
                ) : (
                  <Sun className="h-4 w-4 mr-2" />
                )}
                {theme === "dark" ? "Dark Mode" : "Light Mode"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-destructive cursor-pointer"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
